const amountFields = ["profitContribution", "upgradeFund", "workingFund", "uplineFund", "repurchase"] as const;
export type AmountField = typeof amountFields[number];
type RankClub = "Leader" | "Director" | "Partner" | "Royalty";
export type ClubValue = number | RankClub;
export type TeamKey = "leader" | "director" | "partner";
type MilestoneForm = { sales: number } & Record<AmountField, string>;
type ClubForm = { club: number; nextClub: number | null; name: string; active: boolean; milestones: MilestoneForm[] };
export type RankForm = { rank: RankClub; name: string; percent: string; monthSales: string; targetMonths: string; need: Record<TeamKey, string> };
export type IncomeForm = { revision: number; updatedAt?: string; clubs: ClubForm[]; ranks: RankForm[] };
type Milestone = { sales: number } & Record<AmountField, number>;
type Rank = { rank: RankClub; name: string; percent: number; monthSales: number; targetMonths: number; need: Record<TeamKey, number> };
type MatrixClub = { club: number; nextClub: number | null; name: string; active: boolean; milestones: Milestone[] };
type IncomePlan = { revision: number; updatedAt?: string; clubs: MatrixClub[]; ranks: Rank[] };

export const teamTargets: Record<RankClub, TeamKey[]> = {
  Leader: [], Director: ["leader"], Partner: ["leader", "director"], Royalty: ["leader", "director", "partner"],
};

export const toIncomeForm = (plan: IncomePlan): IncomeForm => ({
  revision: plan.revision,
  updatedAt: plan.updatedAt,
  clubs: plan.clubs.map(club => ({
    club: club.club, nextClub: club.nextClub, name: club.name, active: club.active,
    milestones: club.milestones.map(m => ({ sales: m.sales,
      profitContribution: String(m.profitContribution), upgradeFund: String(m.upgradeFund),
      workingFund: String(m.workingFund), uplineFund: String(m.uplineFund),
      repurchase: String(m.repurchase),
    })),
  })),
  ranks: plan.ranks.map(rank => ({ rank: rank.rank, name: rank.name,
    percent: String(rank.percent), monthSales: String(rank.monthSales), targetMonths: String(rank.targetMonths),
    need: { leader: String(rank.need.leader), director: String(rank.need.director), partner: String(rank.need.partner) },
  })),
});

export const milestoneTotals = (m: MilestoneForm) => ({
  contribution: Math.round(m.sales * Number(m.profitContribution) * 100) / 100,
  allocated: Math.round((Number(m.upgradeFund) + Number(m.workingFund) + Number(m.uplineFund)) * 100) / 100,
  repurchase: Math.round(Number(m.repurchase) * 100) / 100,
});

const validNumber = (value: string, max: number, whole = false) => {
  const number = Number(value);
  return value.trim() !== "" && Number.isFinite(number) && number >= 0 && number <= max &&
    (whole ? Number.isInteger(number) : Math.abs(number * 100 - Math.round(number * 100)) < 0.0001);
};

export const validateIncomeForm = (form: IncomeForm): string[] => {
  const errors: string[] = [];
  form.clubs.forEach(club => {
    if (!club.name.trim() || club.name.trim().length > 80) errors.push(`Club ${club.club}: enter a name of 1–80 characters.`);
    club.milestones.forEach(m => {
      const prefix = `Club ${club.club}, ${m.sales === 9 ? "9 groups × 3" : "3 placement sales"}`;
      if (amountFields.some(key => !validNumber(m[key], 1e12))) {
        errors.push(`${prefix}: enter all amounts as nonnegative numbers with up to two decimals.`);
        return;
      }
      const totals = milestoneTotals(m);
      if (totals.contribution !== totals.allocated) errors.push(`${prefix}: Upgrade + Working + Up-line must equal total contribution.`);
    });
  });
  form.ranks.forEach(rank => {
    const prefix = rank.rank;
    if (!rank.name.trim() || rank.name.trim().length > 80) errors.push(`${prefix}: enter a name of 1–80 characters.`);
    if (!validNumber(rank.percent, 100)) errors.push(`${prefix}: profit share must be 0–100% with up to two decimals.`);
    if (!validNumber(rank.monthSales, 1e6, true)) errors.push(`${prefix}: monthly sales must be a whole number from 0 to 1,000,000.`);
    teamTargets[rank.rank].forEach(key => {
      if (!validNumber(rank.need[key], 1e6, true)) errors.push(`${prefix}: ${key} target must be a whole number from 0 to 1,000,000.`);
    });
    if (rank.rank === "Royalty" && (!validNumber(rank.targetMonths, 120, true) || Number(rank.targetMonths) < 1)) {
      errors.push("Royalty: target window must be 1–120 whole months.");
    }
  });
  if (form.ranks.reduce((sum, rank) => sum + Number(rank.percent), 0) > 100) errors.push("Combined rank profit shares cannot exceed 100%.");
  return errors;
};

// Send only editable fields; GET also contains calculated amounts and metadata.
export const incomePayload = (form: IncomeForm) => ({
  revision: form.revision,
  clubs: [
    ...form.clubs.map(club => ({ club: club.club, name: club.name.trim(), active: club.active,
    milestones: club.milestones.map(m => ({ sales: m.sales,
      profitContribution: Number(m.profitContribution), upgradeFund: Number(m.upgradeFund),
      workingFund: Number(m.workingFund), uplineFund: Number(m.uplineFund),
      repurchase: Number(m.repurchase),
    })),
  })),
  ],
  ranks: form.ranks.map(rank => ({ rank: rank.rank, name: rank.name.trim(), percent: Number(rank.percent),
    monthSales: Number(rank.monthSales), targetMonths: Number(rank.targetMonths),
    need: { leader: Number(rank.need.leader), director: Number(rank.need.director), partner: Number(rank.need.partner) },
  })),
});
